/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import messages from './messages';
import styles from './CloverInput.module.scss';

import { CVV_NAME } from '../forms/CloverForm/constants';

const CloverInput = ({
  id,
  error,
  isLoading,
}: {
  id: string,
  error: string,
  isLoading: boolean,
}) => {
  const { formatMessage } = useIntl();
  const [showTooltip, setShowTooltip] = useState(false);

  const inputContainerRef = useRef<HTMLDivElement | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  useEffect(() => {
    const handleIframeLoad = () => {
      setIframeLoaded(true);
    };

    const observer = new MutationObserver(() => {
      if (inputContainerRef.current) {
        const iframe = inputContainerRef.current.querySelector('iframe');
        if (iframe instanceof HTMLIFrameElement) {
          iframe.addEventListener('load', handleIframeLoad);
          observer.disconnect(); // stop observing after iframe is found
        }
      }
    });

    if (inputContainerRef.current) {
      observer.observe(inputContainerRef.current, {
        childList: true,
        subtree: true,
      });
    }

    return () => observer.disconnect(); // clean up on unmount
  }, []);

  const isTouchDevice = () => !window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  
  
  return (
    <div className={styles['clover-input-container']}>
      {
        id === CVV_NAME ?
          <div
            className={classnames({
              [styles.securityCodeField]: iframeLoaded
            })}
            ref={inputContainerRef}
          >
            <div id="card-cvv" className={styles.iframeContainer} />
            <div className={styles.labelOverlay}>
              <button
                type="button"
                className={styles.tooltipIcon}
                aria-label="info-icon"
                onMouseEnter={() => {
                  if (!isTouchDevice()) {
                    setShowTooltip(true);
                  }
                }}
                onMouseLeave={() => {
                  if (!isTouchDevice()) {
                    setShowTooltip(false);
                  }
                }}
                onFocus={() => {
                  if (!isTouchDevice()) {
                    setShowTooltip(true);
                  }
                }}
                onBlur={() => {
                  if (!isTouchDevice()) {
                    setShowTooltip(false);
                  }
                }}
                onClick={() => setShowTooltip((prev) => !prev) }
              >
                <div className={styles['info-icon']} />
              </button>
              {showTooltip && (
                <div
                  className={styles.tooltipText}
                  onClick={() => {
                    if (isTouchDevice()) {
                      setShowTooltip(false);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setShowTooltip(false);
                    }
                  }}
                  role='button'
                  tabIndex={0}
                >
                  <button
                    type="button"
                    className={styles.closeTooltipButton}
                    aria-label="Close tooltip"
                    onClick={() => setShowTooltip(false)}
                  />
                  <div>
                    {formatMessage(messages.cvvTooltipMessage)}
                  </div>
                </div>
              )}
            </div>
          </div>
        :
        <div id={id} className={classnames(styles['input-container'], {
          [styles['input-container--loading']]: isLoading
        })} />
      }
      
      <div className={classnames(styles['input-errors'], 'input-errors')} id={`${id}-errors`} role="alert">{error}</div>
    </div>
  );
};

export default CloverInput;